import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useTranslation } from 'react-i18next';
import type { ApprovalRequestSchema } from '../../types/approval.type';
import {
  approvalActionTypeLabel,
  approvalModeColor,
  approvalModeLabel,
  approvalOutcomeSummary,
  approvalStatusColor,
  approvalStatusLabel,
  approvalSubmittedAt,
  approvalUserDisplay,
  approvalUserInitial,
  entityTypeLabel,
  formatApprovalDate,
  formatApprovalDateShort,
} from '../../utils/approval.helpers';

type SortField = 'requested_at' | 'status' | 'action_type';
type SortDirection = 'asc' | 'desc';

interface ApprovalsTableProps {
  items: ApprovalRequestSchema[];
  totalCount: number;
  loading: boolean;
  selectedId?: string | null;
  showOutcome?: boolean;
  onRowClick: (id: string) => void;
}

const HEAD_CELL_SX = {
  fontWeight: 600,
  bgcolor: 'background.default',
  whiteSpace: 'nowrap' as const,
  borderBottom: 2,
  borderColor: 'divider',
};

function compareRows(
  a: ApprovalRequestSchema,
  b: ApprovalRequestSchema,
  field: SortField,
  direction: SortDirection,
): number {
  let cmp = 0;
  switch (field) {
    case 'requested_at': {
      const aTime = new Date(approvalSubmittedAt(a) ?? 0).getTime();
      const bTime = new Date(approvalSubmittedAt(b) ?? 0).getTime();
      cmp = aTime - bTime;
      break;
    }
    case 'status':
      cmp = approvalStatusLabel(a.status).localeCompare(approvalStatusLabel(b.status));
      break;
    case 'action_type':
      cmp = approvalActionTypeLabel(a.action_type).localeCompare(
        approvalActionTypeLabel(b.action_type),
      );
      break;
  }
  return direction === 'asc' ? cmp : -cmp;
}

function UserCell({
  username,
  userId,
}: {
  username?: string | null;
  userId?: string | null;
}) {
  const display = approvalUserDisplay(username, userId);
  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.875rem',
          fontWeight: 700,
          bgcolor: 'primary.main',
        }}
      >
        {approvalUserInitial(username, userId)}
      </Avatar>
      <Typography variant="body2" fontWeight={500} noWrap>
        {display}
      </Typography>
    </Stack>
  );
}

export const ApprovalsTable: React.FC<ApprovalsTableProps> = ({
  items,
  totalCount,
  loading,
  selectedId,
  showOutcome = false,
  onRowClick,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('requested_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'requested_at' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((row) => {
      const haystack = [
        row.id,
        row.action_type,
        row.entity_type,
        row.justification,
        row.requested_by,
        row.requested_by_username,
        row.resolved_by_username,
        approvalStatusLabel(row.status),
        approvalModeLabel(row.mode),
        approvalOutcomeSummary(row),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, search]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => compareRows(a, b, sortField, sortDirection));
  }, [filteredItems, sortField, sortDirection]);

  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedItems.slice(start, start + rowsPerPage);
  }, [sortedItems, page, rowsPerPage]);

  const sortDirectionMui = sortDirection === 'asc' ? 'asc' : 'desc';
  const colSpan = showOutcome ? 6 : 5;

  return (
    <Paper
      elevation={2}
      sx={{
        overflow: 'hidden',
        borderRadius: '16px',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <TextField
          size="small"
          placeholder={t('approvals.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 480 }}
        />
      </Box>

      <TableContainer sx={{ maxHeight: 580 }}>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              <TableCell
                sx={HEAD_CELL_SX}
                sortDirection={sortField === 'action_type' ? sortDirectionMui : false}
              >
                <TableSortLabel
                  active={sortField === 'action_type'}
                  direction={sortField === 'action_type' ? sortDirectionMui : 'asc'}
                  onClick={() => handleSort('action_type')}
                >
                  {t('approvals.columns.request')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={HEAD_CELL_SX}>{t('approvals.columns.submittedBy')}</TableCell>
              <TableCell sx={HEAD_CELL_SX}>{t('approvals.columns.entityType')}</TableCell>
              <TableCell
                sx={HEAD_CELL_SX}
                sortDirection={sortField === 'requested_at' ? sortDirectionMui : false}
              >
                <TableSortLabel
                  active={sortField === 'requested_at'}
                  direction={sortField === 'requested_at' ? sortDirectionMui : 'asc'}
                  onClick={() => handleSort('requested_at')}
                >
                  {t('approvals.columns.submitted')}
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={HEAD_CELL_SX}
                sortDirection={sortField === 'status' ? sortDirectionMui : false}
              >
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortDirectionMui : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  {t('approvals.columns.status')}
                </TableSortLabel>
              </TableCell>
              {showOutcome && <TableCell sx={HEAD_CELL_SX}>{t('approvals.columns.outcome')}</TableCell>}
              <TableCell sx={HEAD_CELL_SX} align="right" width={100} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: colSpan + 1 }).map((__, j) => (
                    <TableCell key={j}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!loading && paginatedItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan + 1} align="center" sx={{ py: 8 }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <InboxOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography color="text.secondary" fontWeight={500}>
                      {search.trim() ? t('approvals.noResultsSearch') : t('approvals.noRequests')}
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              paginatedItems.map((row) => {
                const outcome = approvalOutcomeSummary(row);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selectedId === row.id}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected': { bgcolor: 'action.selected' },
                      '&.Mui-selected:hover': { bgcolor: 'action.selected' },
                    }}
                    onClick={() => onRowClick(row.id)}
                  >
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {approvalActionTypeLabel(row.action_type)}
                        </Typography>
                        <Chip
                          label={approvalModeLabel(row.mode)}
                          color={approvalModeColor(row.mode)}
                          size="small"
                          sx={{ height: 20, fontSize: '0.6875rem', width: 'fit-content' }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <UserCell username={row.requested_by_username} userId={row.requested_by} />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {entityTypeLabel(row.entity_type)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={formatApprovalDate(approvalSubmittedAt(row))}>
                        <Typography variant="body2">
                          {formatApprovalDateShort(approvalSubmittedAt(row))}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={approvalStatusLabel(row.status)}
                        color={approvalStatusColor(row.status)}
                        size="small"
                      />
                    </TableCell>
                    {showOutcome && (
                      <TableCell sx={{ maxWidth: 180 }}>
                        {outcome ? (
                          <Stack spacing={0.25}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {outcome}
                            </Typography>
                            {row.resolved_at && (
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {formatApprovalDateShort(row.resolved_at)}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="small"
                        endIcon={<ChevronRightIcon />}
                        onClick={() => onRowClick(row.id)}
                        sx={{ fontWeight: 600, minWidth: 0 }}
                      >
                        {t('approvals.review')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={loading ? 0 : filteredItems.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          `${t('common.rangeOf', { from, to, count })}${
            totalCount > count ? ` (${t('common.loadedCount', { count: totalCount })})` : ''
          }`
        }
      />
    </Paper>
  );
};

export default ApprovalsTable;
